/**
 * Support referrals (disability and aged care intake). Seeded referrals with
 * in-tab mutable state, so a follow-up saved on the detail page sticks for the
 * rest of the session, appears in the History tab, and updates the list tiles.
 * Referred people are invented; none is a real person.
 */
import { addDays, iso, now, ymd } from '../../clock';
import { rng } from '../../rng';
import { getState } from '../../state';
import { COMMUNITY_NAMES, LEADERSHIP, ORG, STAFF, fakeUlid } from '../../world';

export interface Referral {
    id: string;
    string: string;
    archived: boolean;
    refNumber: number;
    dateReceived: string;
    referralSource: string;
    referrerName: string;
    referrerContact: string;
    clientFirstName: string;
    clientLastName: string;
    clientDob: string;
    clientGender: string;
    ndisNumber: string | null;
    currentLocation: string;
    disabilityDx: string;
    supportNeedsSummary: string;
    silHcsType: string;
    fundingInPlan: string;
    fundingAmount: string | null;
    initialContactDate: string | null;
    assessmentCompleted: string;
    assessmentSummary: string | null;
    assessmentOutcome: string | null;
    companyDecision: string | null;
    companyDecisionDate: string | null;
    companyDeclineReason: string | null;
    clientDecision: string | null;
    clientDecisionDate: string | null;
    clientDeclineReason: string | null;
    outcomeStatus: string | null;
    placementStartDate: string | null;
    placementLocation: string | null;
    followUpNotes: string | null;
    typeformResponseToken: string;
    typeformSubmittedAt: string;
    created: string;
    updated: string;
    createdBy: { id: string; string: string } | null;
    updatedBy: { id: string; string: string } | null;
}

export interface ReferralNote {
    id: string;
    message: string;
    created: string;
    updated: string;
    user: { id: string; name: string; string: string; email: string; photo: null };
    file: null;
    files: never[];
}

export interface ReferralLog {
    id: string;
    version: number;
    action: 'create' | 'update';
    username: string | null;
    loggedAt: string;
    data: Record<string, unknown>;
}

export const SIL_HCS_TYPES = ['Supported living – shared', 'Supported living – 1:1', 'Community support – group', 'In-home support', 'Supported living + in-home', 'Respite (STA)', 'TBD'];
export const REFERRAL_SOURCES = ['LAC', 'Support Coordinator', 'Hospital', 'GP', 'Family/Carer', 'NDIS Planner', 'Other Provider', 'Self-Referral', 'Other'];
export const COMPANY_DECISIONS = ['Accept', 'Decline', 'Waitlist', 'Pending', 'Further Assessment Required', 'Deferred'];
export const CLIENT_DECISIONS = ['Accepted', 'Declined', 'Considering', 'No Response', 'Withdrew'];
export const OUTCOME_STATUSES = ['Active Placement', 'Pending Placement', 'Did Not Proceed', 'Referred Elsewhere'];

/** Where each referral sits in the pipeline: [companyDecision, clientDecision, outcomeStatus]. */
type Stage = [string | null, string | null, string | null];
const STAGES: Stage[] = [
    ['Accept', 'Accepted', 'Active Placement'],
    ['Accept', 'Accepted', 'Active Placement'],
    ['Accept', 'Considering', 'Pending Placement'],
    ['Accept', 'Accepted', 'Pending Placement'],
    ['Decline', null, 'Referred Elsewhere'],
    ['Waitlist', null, null],
    ['Further Assessment Required', null, null],
    ['Accept', 'Withdrew', 'Did Not Proceed'],
    [null, null, null],
    [null, null, null],
    [null, null, null],
    [null, null, null],
    [null, null, null],
];

const PEOPLE: [string, string, 'Female' | 'Male'][] = [
    ['Delia', 'Marrin', 'Female'],
    ['Joel', 'Tarrant', 'Male'],
    ['Rosina', 'Hagan', 'Female'],
    ['Mervyn', 'Castles', 'Male'],
    ['Lorraine', 'Petch', 'Female'],
    ['Clement', 'Ardler', 'Male'],
    ['Imelda', 'Rossiter', 'Female'],
    ['Trent', 'Mabbutt', 'Male'],
    ['Vonda', 'Kelleher', 'Female'],
    ['Dorrie', 'Wakeling', 'Female'],
    ['Hilton', 'Gorrie', 'Male'],
    ['Noreen', 'Abernethy', 'Female'],
    ['Errol', 'Sadlier', 'Male'],
];

const NEEDS: [string, string, string, number][] = [
    // [diagnosis, support needs, type, funding]
    ['Acquired brain injury after a car rollover', 'Daily personal care, meal preparation and support to return to community', 'Supported living – 1:1', 248000],
    ['Intellectual disability', 'Support with daily living, shopping and attending the day program', 'Community support – group', 62000],
    ['End-stage kidney disease on dialysis', 'Help with transport to dialysis, meals and medication prompts', 'In-home support', 54000],
    ['Dementia (early stage)', 'Aged care home support, meals and help staying on country', 'In-home support', 48000],
    ['Psychosocial disability', 'Daily check-ins, medication support and help with appointments', 'In-home support', 71000],
    ['Cerebral palsy', 'Personal care, hoist transfers and community access', 'Supported living – shared', 196000],
    ['Stroke with right-sided weakness', 'Mobility support, therapy exercises and personal care', 'Supported living + in-home', 184000],
    ['Foetal alcohol spectrum disorder', 'Routine and life skills support with family', 'Community support – group', 58000],
    ['Spinal cord injury (paraplegia)', 'Personal care, wheelchair-accessible housing and transport', 'Supported living – 1:1', 262000],
    ['Vision impairment and diabetes', 'Medication support, meals and help getting to the clinic', 'In-home support', 39000],
    ['Autism with high support needs', 'Short-term accommodation while the family home is repaired', 'Respite (STA)', 22000],
    ['Frailty and falls risk', 'In-home aged care support and a falls plan', 'In-home support', 44000],
    ['Motor neurone disease', 'Assessment of housing and 24/7 support options', 'TBD', 0],
];

const LOCATIONS = ['Alice Springs Hospital – medical ward', 'Family home', 'Renal hostel in Alice Springs', 'Family home', 'Aged care respite bed', 'Royal Adelaide Hospital – rehab unit', 'Family home', 'Community clinic short-stay room'];

const SOURCE_FOR = ['Hospital', 'Support Coordinator', 'GP', 'Family/Carer', 'LAC', 'Hospital', 'NDIS Planner', 'Family/Carer', 'Hospital', 'GP', 'Support Coordinator', 'Other Provider', 'Self-Referral'];
const REFERRERS = ['Discharge planner', 'Support coordinator', 'Remote area nurse', 'Daughter', 'Local area coordinator', 'Social worker', 'NDIS planner', 'Sister', 'Hospital social worker', 'Clinic GP', 'Support coordinator', 'Aged care assessor', 'Self'];

const careLead = () => STAFF.find(s => !s.endDate && /Support Coordinator|Home Care Coordinator/.test(s.position)) ?? STAFF[0];

function userRef(name: string): { id: string; string: string } {
    return { id: fakeUlid(`user:${name}`), string: name };
}

/** A calendar day as Darwin midnight, whatever the viewer's timezone. */
function day(d: Date): string {
    return `${ymd(d)}T00:00:00+09:30`;
}

function buildReferrals(): Referral[] {
    const t = now();
    const out: Referral[] = [];
    const count = PEOPLE.length;
    for (let i = 0; i < count; i++) {
        const r = rng(`referral:${i}`);
        const [first, last, gender] = PEOPLE[i];
        const [dx, needs, type, funding] = NEEDS[i];
        const [company, client, outcome] = STAGES[i];
        // Oldest first: the decided ones came in months ago, the undecided ones recently.
        const received = addDays(t, -(company ? 150 - i * 11 : 34 - (i - 8) * 6) - r.int(0, 4));
        const contact = addDays(received, r.int(1, 4));
        const decided = company ? addDays(contact, r.int(6, 20)) : null;
        const clientDecided = client && client !== 'Considering' ? addDays(decided as Date, r.int(4, 14)) : null;
        const started = outcome === 'Active Placement' ? addDays(clientDecided as Date, r.int(10, 30)) : null;
        const placement = company === 'Accept' ? r.pick(COMMUNITY_NAMES) : null;
        const referrer = `${REFERRERS[i]} – ${r.pick(['Patel', 'Morgan', 'Hughes', 'Tran', 'Kelly', 'Nash', 'Reid'])}`;
        const ref = 1031 + i;
        const created = new Date(received.getTime() + r.int(8, 16) * 3600000);
        const assessed = !!company && company !== 'Further Assessment Required';
        out.push({
            id: fakeUlid(`referral:${i}`),
            string: `${first} ${last}`,
            archived: false,
            refNumber: ref,
            dateReceived: day(received),
            referralSource: SOURCE_FOR[i],
            referrerName: referrer,
            referrerContact: `referrals${i + 1}@example.org / 08 8900 ${String(1000 + i * 37).slice(0, 4)}`,
            clientFirstName: first,
            clientLastName: last,
            clientDob: day(new Date(t.getFullYear() - r.int(dx.includes('Dementia') || dx.includes('Frailty') ? 66 : 19, dx.includes('Dementia') || dx.includes('Frailty') ? 84 : 58), r.int(0, 11), r.int(1, 28))),
            clientGender: gender,
            ndisNumber: funding > 0 && !/Dementia|Frailty/.test(dx) ? `43${r.int(1000000, 9999999)}` : null,
            currentLocation: LOCATIONS[i % LOCATIONS.length],
            disabilityDx: dx,
            supportNeedsSummary: needs,
            silHcsType: type,
            fundingInPlan: funding > 0 ? 'Yes' : 'Unknown',
            fundingAmount: funding > 0 ? funding.toFixed(2) : null,
            initialContactDate: day(contact),
            assessmentCompleted: assessed ? 'Yes' : company ? 'In progress' : 'No',
            assessmentSummary: assessed ? `Home visit completed at ${placement ?? 'the family home'}; family and interpreter present.` : null,
            assessmentOutcome: !company ? null : company === 'Decline' ? 'Not Suitable' : company === 'Further Assessment Required' ? 'Further Assessment Required' : 'Suitable',
            companyDecision: company,
            companyDecisionDate: decided ? day(decided) : null,
            companyDeclineReason: company === 'Decline' ? 'Needs 24/7 nursing care beyond our service model; referred to residential aged care.' : null,
            clientDecision: client,
            clientDecisionDate: clientDecided ? day(clientDecided) : null,
            clientDeclineReason: client === 'Withdrew' ? 'Family chose to support at home with kin.' : null,
            outcomeStatus: outcome,
            placementStartDate: started ? day(started) : null,
            placementLocation: placement,
            followUpNotes: company === 'Accept' && outcome === 'Pending Placement' ? 'Family meeting with interpreter booked; waiting on NDIS plan review.' : null,
            typeformResponseToken: `demo-token-${ref}`,
            typeformSubmittedAt: iso(created),
            created: iso(new Date(created.getTime() + 5000)),
            updated: iso(clientDecided ?? decided ?? new Date(created.getTime() + 5000)),
            createdBy: null,
            updatedBy: company ? userRef(LEADERSHIP.care.name) : null,
        });
    }
    return out;
}

/** Live referral store (mutations persist for the tab). */
export function referrals(): Referral[] {
    return getState<Referral[]>('referrals', buildReferrals);
}

export function referralById(id: string): Referral | undefined {
    return referrals().find(r => r.id === id);
}

export function referralIds(): string[] {
    return referrals().map(r => r.id);
}

/** Collection view (default + collection groups only). */
export function referralListItem(r: Referral): Record<string, unknown> {
    return {
        id: r.id,
        string: r.string,
        archived: r.archived,
        refNumber: r.refNumber,
        dateReceived: r.dateReceived,
        referralSource: r.referralSource,
        referrerName: r.referrerName,
        clientFirstName: r.clientFirstName,
        clientLastName: r.clientLastName,
        silHcsType: r.silHcsType,
        assessmentOutcome: r.assessmentOutcome,
        companyDecision: r.companyDecision,
        companyDecisionDate: r.companyDecisionDate,
        clientDecision: r.clientDecision,
        clientDecisionDate: r.clientDecisionDate,
        placementStartDate: r.placementStartDate,
        outcomeStatus: r.outcomeStatus,
    };
}

/** Filtered, sorted collection for GET /api/v1/sil_referrals. */
export function referralCollection(query: URLSearchParams): Record<string, unknown>[] {
    let list = referrals().filter(r => (query.get('archived') === 'true' ? r.archived : !r.archived));
    const search = (query.get('simplesearch') || '').trim().toLowerCase();
    if (search) {
        list = list.filter(r => [r.string, r.referrerName, String(r.refNumber), r.referralSource, r.silHcsType].some(v => v.toLowerCase().includes(search)));
    }
    const filter = query.get('filter');
    if (filter) {
        try {
            const f = JSON.parse(filter) as Record<string, string>;
            for (const key of ['companyDecision', 'outcomeStatus', 'silHcsType', 'referralSource'] as const) {
                if (f[key]) {
                    list = list.filter(r => r[key] === f[key]);
                }
            }
        } catch {
            // ignore a malformed filter, like the API does
        }
    }
    let field = 'dateReceived';
    let dir = 'desc';
    query.forEach((v, k) => {
        const m = /^order\[(\w+)\]$/.exec(k);
        if (m) {
            field = m[1];
            dir = v.toLowerCase() === 'asc' ? 'asc' : 'desc';
        }
    });
    const sorted = [...list].sort((a, b) => {
        const av = (a as unknown as Record<string, unknown>)[field];
        const bv = (b as unknown as Record<string, unknown>)[field];
        const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av ?? '').localeCompare(String(bv ?? ''));
        return dir === 'asc' ? cmp : -cmp;
    });
    return sorted.map(referralListItem);
}

/** Pipeline summary for the AI assistant / reports. */
export function referralPipeline(): { total: number; awaiting: number; accepted: number; active: number; declined: number; pending_placement: number } {
    const list = referrals().filter(r => !r.archived);
    return {
        total: list.length,
        awaiting: list.filter(r => !r.companyDecision).length,
        accepted: list.filter(r => r.companyDecision === 'Accept').length,
        active: list.filter(r => r.outcomeStatus === 'Active Placement').length,
        declined: list.filter(r => r.companyDecision === 'Decline').length,
        pending_placement: list.filter(r => r.outcomeStatus === 'Pending Placement').length,
    };
}

/* ------------------------------------------------------------------ */
/* Follow-up form (Symfony FormView)                                   */
/* ------------------------------------------------------------------ */

const FORM = 'sil_referral';

function field(name: string, type: string, value: unknown, label: string, extra: Record<string, unknown> = {}): Record<string, unknown> {
    const prefixes = type === 'textarea' ? ['form', 'text', 'textarea', `_${FORM}_${name}`] : ['form', type, `_${FORM}_${name}`];
    return {
        vars: {
            id: `${FORM}_${name}`,
            name,
            full_name: `${FORM}[${name}]`,
            value,
            label,
            attr: {},
            row_attr: { class: 'mb-3' },
            label_attr: {},
            required: false,
            disabled: false,
            errors: [],
            help: null,
            compound: false,
            valid: true,
            block_prefixes: prefixes,
            ...extra,
        },
        children: {},
    };
}

function choice(name: string, value: string | null, label: string, options: string[]): Record<string, unknown> {
    return field(name, 'choice', value ?? '', label, {
        multiple: false,
        expanded: false,
        placeholder: '— Not decided —',
        choices: options.map(o => ({ label: o, value: o, data: o, attr: {}, labelTranslationParameters: [] })),
    });
}

const dateOnly = (v: string | null) => (v ? ymd(new Date(v)) : '');

export function referralFormView(r: Referral): Record<string, unknown> {
    const children: Record<string, unknown> = {
        companyDecision: choice('companyDecision', r.companyDecision, 'NPY decision', COMPANY_DECISIONS),
        companyDecisionDate: field('companyDecisionDate', 'date', dateOnly(r.companyDecisionDate), 'Decision date', { type: 'date', widget: 'single_text' }),
        companyDeclineReason: field('companyDeclineReason', 'textarea', r.companyDeclineReason ?? '', 'Decline reason'),
        clientDecision: choice('clientDecision', r.clientDecision, 'Client decision', CLIENT_DECISIONS),
        clientDecisionDate: field('clientDecisionDate', 'date', dateOnly(r.clientDecisionDate), 'Decision date', { type: 'date', widget: 'single_text' }),
        clientDeclineReason: field('clientDeclineReason', 'textarea', r.clientDeclineReason ?? '', 'Decline reason'),
        outcomeStatus: choice('outcomeStatus', r.outcomeStatus, 'Outcome status', OUTCOME_STATUSES),
        placementStartDate: field('placementStartDate', 'date', dateOnly(r.placementStartDate), 'Placement start date', { type: 'date', widget: 'single_text' }),
        placementLocation: field('placementLocation', 'text', r.placementLocation ?? '', 'Placement location'),
        followUpNotes: field('followUpNotes', 'textarea', r.followUpNotes ?? '', 'Follow-up notes'),
    };
    const value: Record<string, unknown> = {};
    for (const [k, c] of Object.entries(children)) {
        value[k] = (c as { vars: { value: unknown } }).vars.value;
    }
    return {
        vars: {
            id: FORM,
            name: FORM,
            full_name: FORM,
            value,
            attr: {},
            row_attr: {},
            label_attr: {},
            required: true,
            disabled: false,
            errors: [],
            compound: true,
            valid: true,
            submitted: false,
            block_prefixes: ['form', FORM],
        },
        children,
    };
}

const FORM_FIELDS = ['companyDecision', 'companyDecisionDate', 'companyDeclineReason', 'clientDecision', 'clientDecisionDate', 'clientDeclineReason', 'outcomeStatus', 'placementStartDate', 'placementLocation', 'followUpNotes'] as const;
const DATE_FIELDS = new Set(['companyDecisionDate', 'clientDecisionDate', 'placementStartDate']);

/** POST /api/sil-referral/form/{id}: merge the follow-up into the store and log it. */
export function saveReferralFollowUp(r: Referral, body: Record<string, unknown>, editor: string): Referral {
    const logs = referralLogs(r.id); // materialise the seeded history before changing anything
    const changed: Record<string, unknown> = {};
    const target = r as unknown as Record<string, unknown>;
    for (const key of FORM_FIELDS) {
        if (!(key in (body || {}))) continue;
        let v = body[key];
        if (v === '' || v === undefined) v = null;
        if (v !== null && DATE_FIELDS.has(key)) {
            const s = String(v).slice(0, 10);
            v = /^\d{4}-\d{2}-\d{2}$/.test(s) ? iso(new Date(`${s}T00:00:00`)) : null;
        }
        if (target[key] !== v) {
            target[key] = v;
            changed[key] = v;
        }
    }
    if (Object.keys(changed).length) {
        r.updated = iso(now());
        r.updatedBy = userRef(editor);
        logs.unshift({ id: fakeUlid(`log:${r.id}:${logs.length + 1}`), version: logs.length + 1, action: 'update', username: editor, loggedAt: r.updated, data: changed });
    }
    return r;
}

/** GET /api/ext/filter/form/App%5CEntity%5CSilReferral — the list's Filter modal. */
export function referralFilterFormView(): Record<string, unknown> {
    const name = 'sil_referral_filter';
    const pick = (key: string, label: string, options: string[]) => {
        const f = choice(key, null, label, options) as { vars: Record<string, unknown> };
        f.vars['id'] = `${name}_${key}`;
        f.vars['full_name'] = `${name}[${key}]`;
        f.vars['placeholder'] = 'Any';
        f.vars['block_prefixes'] = ['form', 'choice', `_${name}_${key}`];
        return f;
    };
    return {
        vars: { id: name, name, full_name: name, value: {}, attr: {}, row_attr: {}, label_attr: {}, required: false, disabled: false, errors: [], compound: true, valid: true, submitted: false, block_prefixes: ['form', name] },
        children: {
            companyDecision: pick('companyDecision', 'NPY decision', COMPANY_DECISIONS),
            outcomeStatus: pick('outcomeStatus', 'Outcome', OUTCOME_STATUSES),
            silHcsType: pick('silHcsType', 'Support type', SIL_HCS_TYPES),
            referralSource: pick('referralSource', 'Referral source', REFERRAL_SOURCES),
        },
    };
}

/* ------------------------------------------------------------------ */
/* Notes and history tabs                                              */
/* ------------------------------------------------------------------ */

function noteUser(name: string): ReferralNote['user'] {
    return { id: fakeUlid(`user:${name}`), name, string: name, email: `${name.toLowerCase().replace(/[^a-z]+/g, '.')}@${ORG.domain}`, photo: null };
}

export function referralNotes(id: string): ReferralNote[] {
    const all = getState<Record<string, ReferralNote[]>>('referralNotes', () => ({}));
    if (!all[id]) {
        const r = referralById(id);
        const notes: ReferralNote[] = [];
        if (r) {
            const rr = rng(`referral:notes:${id}`);
            const received = new Date(r.dateReceived);
            const lead = careLead().name;
            // Seeded from the referral's original stage, not any follow-up saved this session.
            const seededDecision = STAGES[r.refNumber - 1031]?.[0] ?? null;
            const lines = [
                `Called the referrer to confirm details; ${r.clientFirstName} would like support close to family.`,
                'Interpreter booked for the first meeting (Pitjantjatjara).',
                seededDecision === 'Accept' ? 'Intake meeting held with family. Talked through the service agreement and next steps.' : 'Waiting on recent clinic notes before the assessment.',
            ];
            const k = seededDecision ? 3 : rr.int(1, 2);
            for (let i = 0; i < k; i++) {
                const when = addDays(received, 1 + i * rr.int(2, 6));
                if (when > now()) {
                    break;
                }
                const at = iso(when);
                notes.unshift({ id: fakeUlid(`note:${id}:${i}`), message: lines[i], created: at, updated: at, user: noteUser(i === 2 ? LEADERSHIP.care.name : lead), file: null, files: [] });
            }
        }
        all[id] = notes;
    }
    return all[id];
}

export function addReferralNote(id: string, message: string, author: string): ReferralNote {
    const at = iso(now());
    const note: ReferralNote = { id: fakeUlid(`note:${id}:new:${referralNotes(id).length}`), message, created: at, updated: at, user: noteUser(author), file: null, files: [] };
    referralNotes(id).unshift(note);
    return note;
}

export function referralLogs(id: string): ReferralLog[] {
    const all = getState<Record<string, ReferralLog[]>>('referralLogs', () => ({}));
    if (!all[id]) {
        const r = referralById(id);
        const logs: ReferralLog[] = [];
        if (r) {
            logs.push({
                id: fakeUlid(`log:${id}:1`),
                version: 1,
                action: 'create',
                username: null,
                loggedAt: r.created,
                data: { refNumber: r.refNumber, clientFirstName: r.clientFirstName, clientLastName: r.clientLastName, referralSource: r.referralSource, silHcsType: r.silHcsType },
            });
            if (r.companyDecision) {
                logs.unshift({ id: fakeUlid(`log:${id}:2`), version: 2, action: 'update', username: LEADERSHIP.care.name, loggedAt: r.companyDecisionDate ?? r.updated, data: { companyDecision: r.companyDecision, companyDecisionDate: r.companyDecisionDate } });
            }
            if (r.clientDecision || r.outcomeStatus) {
                logs.unshift({ id: fakeUlid(`log:${id}:3`), version: logs.length + 1, action: 'update', username: LEADERSHIP.care.name, loggedAt: r.updated, data: { clientDecision: r.clientDecision, outcomeStatus: r.outcomeStatus } });
            }
        }
        all[id] = logs;
    }
    return all[id];
}
